import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ShoppingBag, ShoppingCart, MessageCircle, CheckCircle, Users, Globe, Sparkles, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function LandingPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [counts, setCounts] = useState({ companies: 0, offers: 0, deals: 0 });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Animated counter
    const duration = 2000;
    const targets = { companies: 1000, offers: 5000, deals: 10000 };
    const steps = 60;
    const interval = duration / steps;

    let current = { companies: 0, offers: 0, deals: 0 };
    const increment = {
      companies: targets.companies / steps,
      offers: targets.offers / steps,
      deals: targets.deals / steps
    };

    const timer = setInterval(() => {
      current.companies = Math.min(current.companies + increment.companies, targets.companies);
      current.offers = Math.min(current.offers + increment.offers, targets.offers);
      current.deals = Math.min(current.deals + increment.deals, targets.deals);
      
      setCounts({
        companies: Math.floor(current.companies),
        offers: Math.floor(current.offers),
        deals: Math.floor(current.deals)
      });

      if (current.companies >= targets.companies) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/80 backdrop-blur-xl border-b border-border shadow-sm' 
            : 'bg-transparent'
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center">
                <Building2 size={20} className="text-white sm:hidden" />
                <Building2 size={24} className="text-white hidden sm:block" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl sm:text-2xl font-heading font-black tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent leading-tight">
                  {t('appName')}
                </span>
                <span className="hidden sm:block text-[10px] font-medium text-muted-foreground">{language === 'ar' ? 'البوابـة إلى الشركات العراقية' : 'Your Gateway to Iraqi Business'}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="language-toggle"
              >
                <Globe size={18} />
                {language === 'ar' ? 'English' : 'عربي'}
              </button>
              <Link to="/login" data-testid="login-link">
                <button className="hidden sm:inline-block px-5 py-2.5 text-sm font-medium text-foreground hover:text-accent transition-colors">
                  {t('login')}
                </button>
              </Link>
              <Link to="/register" data-testid="register-link">
                <button className="px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold bg-gradient-to-r from-accent to-accent/90 text-white rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                  {t('register')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20 pb-32 overflow-hidden" 
        data-testid="hero-section"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-accent/20 rounded-full mb-4 animate-fade-up">
              <Sparkles size={16} className="text-accent" />
              <span className="text-sm font-medium text-accent">Leading B2B Marketplace</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-black tracking-tight mb-2 sm:mb-3 animate-fade-up" style={{animationDelay: '0.1s'}}>
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                {t('heroTitle')}
              </span>
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-heading font-bold bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent mb-6 sm:mb-8 animate-fade-up" style={{animationDelay: '0.15s'}}>
              {language === 'ar' ? 'البوابـة إلى الشركات العراقية' : 'Your Gateway to Iraqi Business'}
            </p>

            {/* Subtitle */}
            <p className="text-base sm:text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8 sm:mb-10 max-w-3xl mx-auto animate-fade-up px-2" style={{animationDelay: '0.2s'}}>
              {t('heroSubtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 animate-fade-up" style={{animationDelay: '0.3s'}}>
              <Link to="/register" data-testid="hero-register-btn">
                <button className="group px-8 py-4 bg-gradient-to-r from-accent to-accent/90 text-white text-lg font-semibold rounded-xl hover:shadow-2xl hover:shadow-accent/30 hover:-translate-y-1 transition-all duration-200 flex items-center gap-2">
                  {t('getStarted')}
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/marketplace" data-testid="hero-marketplace-btn">
                <button className="px-8 py-4 bg-white/80 backdrop-blur-sm border border-border text-foreground text-lg font-semibold rounded-xl hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                  {t('browseOffers')}
                </button>
              </Link>
            </div>

            {/* Download App */}
            <div className="mb-16 animate-fade-up" style={{animationDelay: '0.35s'}}>
              <a
                href="https://play.google.com/store/apps/details?id=app.emergent.arabicb2bhub33ace521"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-play-btn"
                className="inline-flex items-center gap-3 px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-900 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.196 12l2.502-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <div className={`${language === 'ar' ? 'text-right' : 'text-left'}`}>
                  <div className="text-[10px] leading-tight opacity-80">{language === 'ar' ? 'متوفر على' : 'GET IT ON'}</div>
                  <div className="text-base font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-muted-foreground animate-fade-up" style={{animationDelay: '0.4s'}}>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <CheckCircle size={16} className="text-success" />
                <span>Verified Companies</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Star size={16} className="text-yellow-500" />
                <span>Top Rated</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <CheckCircle size={16} className="text-success" />
                <span>Secure Platform</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-accent/30 rounded-full p-1">
            <div className="w-1.5 h-3 bg-accent/50 rounded-full mx-auto"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3 sm:mb-4 font-semibold">{t('features')}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black text-primary mb-4">
              {t('whyChooseUs')}
            </h2>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 - Large */}
            <div className="lg:col-span-2 lg:row-span-2 group relative bg-gradient-to-br from-accent/5 to-accent/10 p-8 rounded-2xl border border-accent/20 hover:border-accent/40 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <ShoppingBag size={32} className="text-white" />
                </div>
                <h3 className="text-3xl font-heading font-bold text-primary mb-4">{t('saleOffers')}</h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {t('saleOffersDesc')}
                </p>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="lg:col-span-1 group relative bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200/50 hover:border-green-300 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <ShoppingCart size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-bold text-primary mb-3">{t('purchaseRequests')}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('purchaseRequestsDesc')}
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="lg:col-span-1 group relative bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200/50 hover:border-purple-300 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageCircle size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-bold text-primary mb-3">{t('directMessaging')}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('directMessagingDesc')}
              </p>
            </div>
            
            {/* Feature 4 */}
            <div className="lg:col-span-2 group relative bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border border-blue-200/50 hover:border-blue-300 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <CheckCircle size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-heading font-bold text-primary mb-3">{t('trustQuality')}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {t('trustQualityDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary/90 text-white relative overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <Users size={32} className="opacity-90 sm:hidden" />
                <Users size={40} className="opacity-90 hidden sm:block" />
              </div>
              <p className="text-4xl sm:text-6xl font-heading font-black mb-2 sm:mb-3">{counts.companies.toLocaleString()}+</p>
              <p className="text-sm sm:text-lg opacity-90">{t('registeredCompanies')}</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <ShoppingBag size={32} className="opacity-90 sm:hidden" />
                <ShoppingBag size={40} className="opacity-90 hidden sm:block" />
              </div>
              <p className="text-4xl sm:text-6xl font-heading font-black mb-2 sm:mb-3">{counts.offers.toLocaleString()}+</p>
              <p className="text-sm sm:text-lg opacity-90">{t('activeOffers')}</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <CheckCircle size={32} className="opacity-90 sm:hidden" />
                <CheckCircle size={40} className="opacity-90 hidden sm:block" />
              </div>
              <p className="text-4xl sm:text-6xl font-heading font-black mb-2 sm:mb-3">{counts.deals.toLocaleString()}+</p>
              <p className="text-sm sm:text-lg opacity-90">{t('completedDeals')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-accent/5 via-purple-50/20 to-blue-50/20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-accent/20 rounded-full mb-8">
            <Sparkles size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">Start Your Journey Today</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black text-primary mb-4 sm:mb-6">
            {t('joinThousands')}
          </h2>
          <p className="text-base sm:text-xl md:text-2xl text-muted-foreground mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
            {t('startPosting')}
          </p>
          
          <Link to="/register" data-testid="cta-register-btn">
            <button className="group px-10 py-5 bg-gradient-to-r from-accent to-accent/90 text-white text-xl font-bold rounded-xl hover:shadow-2xl hover:shadow-accent/30 hover:-translate-y-1 transition-all duration-200 flex items-center gap-3 mx-auto">
              {t('registerFree')}
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-12 border-t border-primary/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <Building2 size={24} />
            </div>
            <span className="text-2xl font-heading font-bold">{t('appName')}</span>
          </div>
          <p className="text-white/70">&copy; 2024 {t('appName')}. {t('allRightsReserved')}</p>
          <Link to="/privacy-policy" className="inline-block mt-3 text-sm text-white/50 hover:text-white/80 transition-colors underline">
            {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Link>
        </div>
      </footer>
    </div>
  );
}
